import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { fetchLightningRecord } from './lightningRecord'
import { RootState, useDispatch, useSelector } from '../../store'
import {
  updateAppAlert,
  getAccount,
  PartnerType,
  loadAvailableAccounts,
  setSelectedAccount,
  type AccountDataState,
  type AccountDataSelection as AccountData,
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  startLoading,
  stopLoading,
  LoadingTypes,
  useCurrentCloudProvider,
  useCloseSalesforceModalAfterSave,
  actionTypeWrapper,
  type CloudType,
  useSalesforceContext,
  type CloudAccountChangeParams,
  useAccountId,
  LayoutWrapper,
  SelectedCloudIcon,
  RouteApp,
} from '@labrav/common'
import { isEmpty } from 'lodash'
import { ModalToCreatePrivateOffer } from '../../common/components/ModalToCreatePrivateOffer/ModalToCreatePrivateOffer'
import { CloudAndProductNameTitle } from '../../components/CloudAndProductNameTitle'
import { SubscriptionSelectionModal } from '../../components/Subscription/SubscriptionSelectionModal/SubscriptionSelectionModal'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import type { ModalSubscriptionsState as SubscriptionsState } from '../../modules/modalSubscription/reducer'
import { getActiveReceivedResaleAuthorizations } from '../../modules/receivedResellerAuthorization/action'
import {
  setCloudMarketplaceProductListing,
  updateProductIdOnFilters,
} from '../../modules/productListingFilter/action'
import { FlyoutFunction } from '../../modules/productsListing/types'
import {
  EditPrivateOfferWrapper,
  NewPrivateOfferWrapper,
} from '../../components/NewPrivateOfferWrapper'
import {
  DetailedViewPrivateOfferWrapper,
  EditPrivateOfferWrapperV2,
  RenewOfferSubscriptionWrapper,
  SubscriptionDetailWrapper,
} from '../../routes/routesComponents'
import {
  clearProductsListing,
  getProducts,
  setSelectedProductOnListing,
} from '../../modules/productsListing/action'
import { getCurrentPrivateOffer } from '../../modules/privateOffer/action'
import PrivateOffersWrapper from '../PrivateOffersWrapper'
import { buildSalesforceCosellReferralDetailsCosellSegment } from './salesforceCosellReferralPathUtils'
import { PlanType, LoadingLogo } from '@labrav/react-components'
import { HandshakeOutlined } from '@mui/icons-material'
import { CRMTypes } from '../../utils/constants'

export const PrivateOfferWrapper: React.FC = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [openPrivateOfferModal, setOpenPrivateOfferModal] =
    useState<boolean>(true)
  const cloudMarketplace = useCurrentCloudProvider() as CloudType
  const navigate = useNavigate()
  const cloudAccountId = new URLSearchParams(search).get('cloudAccountId') || ''
  const accounts = useSelector<AccountData[]>(
    (state: RootState) =>
      state.accountSelection.availableAccounts as AccountData[]
  )
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const accountIdFromSelection = useAccountId({ product: 'flyout' }) || ''
  const currentAccountId = cloudAccountId || accountIdFromSelection

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    selectedCloudTypeProductListing
  )

  const handleCreatePrivateOffer = () => {
    setOpenPrivateOfferModal(false)
    const externalBaseURL = `/external-apps/salesforce/partners/${currentAccountId}/flyout`
    navigate({
      pathname: `${externalBaseURL}/products-listing/${selectedProductId}/private-offers/edit`,
      search,
    })
  }

  useEffect(() => {
    if (cloudMarketplace) {
      const accountData = (Array.isArray(accounts) ? accounts : []).filter(
        (account: AccountData) => account?.product?.toLowerCase() === 'flyout'
      )
      const initialAccountId =
        accountData?.find(
          (account: AccountData) => account?.cloud === cloudMarketplace
        )?.id || ''
      dispatch(
        setSelectedAccount(
          initialAccountId,
          'flyout',
          cloudMarketplace as CloudType
        )
      )
    }
  }, [])

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      // Extract original accountId from the combined format "accountId-product-cloud"
      const accountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params

      await dispatch(startLoading(LoadingTypes.GENERAL))
      await dispatch(
        setSelectedAccount(accountId, 'flyout', cloud as CloudType)
      )
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))
      // await dispatch(clearPrivateOffers())
      await dispatch(getProducts(accountId, cloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [dispatch, navigate, selectedCloudTypeProductListing]
  )

  const handleProductChange = useCallback(
    async (productId: string) => {
      setSelectedProductId(productId)

      dispatch(
        updateProductIdOnFilters(
          productId,
          selectedCloudTypeProductListing,
          FlyoutFunction.PRIVATE_OFFERS
        )
      )
    },
    [selectedCloudTypeProductListing]
  )

  // Render Action Hub layout (data is now loaded in Redux)
  return (
    <LayoutWrapper isExternal>
      <ModalToCreatePrivateOffer
        open={openPrivateOfferModal}
        hideCancelIcon={true}
        handleCreatePrivateOffer={handleCreatePrivateOffer}
        handleProductChange={handleProductChange}
        handleCloudAccountChange={handleCloudAccountChange}
        selectedAccountIdWithCloud={selectedAccountIdWithCloud}
        isCloudDisabled={!isEmpty(cloudAccountId)}
        accountId={currentAccountId}
      />
    </LayoutWrapper>
  )
}

export const SalesforcePrivateOfferCreation: React.FC = () => {
  const { partnerId, productId } = useParams<{
    partnerId: string
    productId: string
  }>()
  const dispatch = useDispatch()
  const { search } = useLocation()
  const { record, actions } = useSalesforceContext()

  useCloseSalesforceModalAfterSave({
    resourceType: 'offer',
    closeModal: actions?.closeModal,
  })
  const { partnerData } = useSelector<AccountDataState>(
    state => state.AccountData.user as AccountDataState
  )
  const cloudMarketplace = useCurrentCloudProvider()

  const crmOpportunityId = record?.recordId || ''
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const loadPartnerData = async () => {
    try {
      // Load partner account data if not already loaded or if different partnerId
      if (isEmpty(partnerData) || partnerData?.partnerId !== partnerId) {
        await dispatch(
          getAccount(partnerId as string, PartnerType.User) as never
        )
      }

      // Load available accounts for account selection
      await dispatch(loadAvailableAccounts(partnerId as string) as never)
    } catch (error) {
      console.error('Failed to load partner data:', error)
    }
  }

  useEffect(() => {
    if (isEmpty(fallbackAccountId)) {
      loadPartnerData()
    }
  }, [fallbackAccountId])

  // Ensure useAccountId({ product: 'flyout' }) inside the form returns the Salesforce
  // partnerId so that emitSalesforceResourceSaved emits with the same ID the ActionHub
  // has, keeping the refresh event partnerId check aligned.
  useEffect(() => {
    if (partnerId && cloudMarketplace) {
      dispatch(
        setSelectedAccount(partnerId, 'flyout', cloudMarketplace as CloudType)
      )
    }
  }, [partnerId, cloudMarketplace, dispatch])
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const selectedProduct = products.find(
    productItem => productItem.productId === productId
  )
  const v2Offer =
    cloudMarketplace === 'AZURE' ||
    (cloudMarketplace === 'AWS' &&
      (selectedProduct?.productType === PlanType.CONTAINER ||
        selectedProduct?.productType === PlanType.SAAS_CONTRACT ||
        selectedProduct?.productType === PlanType.AMI ||
        selectedProduct?.productType === PlanType.SAAS_SUBSCRIPTION))
  const externalBaseURL = `/external-apps/salesforce/partners/${partnerId}/flyout/products-listing`
  const fromValue = new URLSearchParams(search).get('from') || ''
  return (
    <div data-testid="create-edit-wrapper">
      {v2Offer ? (
        <LayoutWrapper
          title={
            fromValue === 'clone'
              ? 'Clone private offer'
              : 'Create a private offer'
          }
          isBack={false}
          actions={SelectedCloudIcon}
          isSidePanelOpened={false}
          isExternal={fromValue !== 'clone'}
        >
          <EditPrivateOfferWrapperV2
            crmOpportunityId={crmOpportunityId}
            externalBaseURL={externalBaseURL}
            search={search}
            isSidePanelVisible={fromValue === 'clone'}
          />
        </LayoutWrapper>
      ) : (
        <RouteApp
          titleIcon={<HandshakeOutlined fontSize="medium" />}
          title="New private offer"
          actions={CloudAndProductNameTitle}
          withOpenSidePanel={false}
        >
          <EditPrivateOfferWrapper
            crmOpportunityId={crmOpportunityId}
            externalBaseURL={externalBaseURL}
            search={search}
          />
        </RouteApp>
      )}
    </div>
  )
}

export const SalesforceViewPrivateOffer: React.FC = () => {
  const { partnerId: partnerIdFromParams } = useParams<{
    partnerId: string
  }>()
  const selectedCloud = useCurrentCloudProvider()
  const { search } = useLocation()
  const { record } = useSalesforceContext()
  const crmOpportunityId = record?.recordId || ''
  const { partnerId } = useSalesforceContext()
  const currentPartnerId = partnerIdFromParams || partnerId
  const { productId, privateOfferId } = useParams<{
    productId: string
    privateOfferId: string
  }>()
  const dispatch = useDispatch()
  const externalBaseURL = `/external-apps/salesforce/partners/${currentPartnerId}/flyout/products-listing/${productId}`
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const cloudMarketplace = useCurrentCloudProvider()
  useEffect(() => {
    if (!isEmpty(currentPartnerId) && isEmpty(products) && cloudMarketplace) {
      dispatch(getProducts(currentPartnerId, cloudMarketplace))
    }
  }, [currentPartnerId, products, cloudMarketplace, dispatch])

  useEffect(() => {
    const loadCurrentPrivateOffer = async () => {
      if (!privateOfferId) return

      try {
        await dispatch(
          getCurrentPrivateOffer(
            currentPartnerId,
            privateOfferId,
            selectedCloud,
            productId
          )
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load current private offer', err)
      }
    }

    void loadCurrentPrivateOffer()
  }, [partnerId, privateOfferId, selectedCloud, productId])

  return (
    <PrivateOffersWrapper>
      {selectedCloud === 'AZURE' || selectedCloud === 'AWS' ? (
        <DetailedViewPrivateOfferWrapper
          crmOpportunityId={crmOpportunityId}
          externalBaseURL={externalBaseURL}
          search={search}
          crmType={CRMTypes.salesforce}
        />
      ) : (
        <NewPrivateOfferWrapper
          crmOpportunityId={crmOpportunityId}
          externalBaseURL={externalBaseURL}
        />
      )}
    </PrivateOffersWrapper>
  )
}

export const SalesforceEditPrivateOffer: React.FC = () => {
  const dispatch = useDispatch()
  const selectedCloud = useCurrentCloudProvider()
  const { search } = useLocation()
  const { record, partnerId, actions } = useSalesforceContext()

  useCloseSalesforceModalAfterSave({
    resourceType: 'offer',
    closeModal: actions?.closeModal,
  })
  const { partnerData } = useSelector<AccountDataState>(
    state => state.AccountData.user as AccountDataState
  )
  const crmOpportunityId = record?.recordId || ''
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const externalBaseURL = `/external-apps/salesforce/partners/${partnerId}/flyout/products-listing`
  const fromValue = new URLSearchParams(search).get('from') || ''
  // Load partner data if needed
  useEffect(() => {
    const loadPartnerData = async () => {
      if (!partnerId) return

      try {
        // Load partner account data if not already loaded or if different partnerId
        if (isEmpty(partnerData) || partnerData?.partnerId !== partnerId) {
          await dispatch(
            getAccount(partnerId as string, PartnerType.User) as never
          )
        }

        // Load available accounts for account selection
        await dispatch(loadAvailableAccounts(partnerId as string) as never)
      } catch (error) {
        console.error('Failed to load partner data:', error)
      }
    }

    if (isEmpty(fallbackAccountId)) {
      void loadPartnerData()
    }
  }, [fallbackAccountId, partnerId, partnerData?.partnerId, dispatch])

  useEffect(() => {
    if (selectedCloud) {
      dispatch(
        setSelectedAccount(
          fallbackAccountId,
          'flyout',
          selectedCloud as CloudType
        )
      )
    }
  }, [selectedCloud, dispatch])

  return (
    <div data-testid="edit-private-offer-wrapper">
      {selectedCloud === 'AZURE' || selectedCloud === 'AWS' ? (
        <LayoutWrapper
          title={
            fromValue === 'clone'
              ? 'Clone private offer'
              : 'Update private offer'
          }
          isBack={false}
          actions={SelectedCloudIcon}
          isSidePanelOpened={false}
          isExternal={fromValue !== 'clone'}
        >
          <EditPrivateOfferWrapperV2
            crmOpportunityId={crmOpportunityId}
            externalBaseURL={externalBaseURL}
            search={search}
            isSidePanelVisible={fromValue === 'clone'}
          />
        </LayoutWrapper>
      ) : (
        <LayoutWrapper
          title={
            fromValue === 'clone'
              ? 'Clone private offer'
              : 'Update private offer'
          }
          isBack={false}
          actions={SelectedCloudIcon}
          isSidePanelOpened={false}
          isExternal={fromValue !== 'clone'}
        >
          <EditPrivateOfferWrapper
            crmOpportunityId={crmOpportunityId}
            externalBaseURL={externalBaseURL}
            search={search}
          />
        </LayoutWrapper>
      )}
    </div>
  )
}

export const SalesforceABOSubscriptionModal: React.FC = () => {
  const [selectedSubscription, setSelectedSubscription] = useState<string>('')
  const { subscriptions } = useSelector<SubscriptionsState>(
    state => state.modalSubscription
  )
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { partnerId: currentAccountId } = useSalesforceContext()

  const acceptSelectedSubscription = async (subscriptionId: string) => {
    const selectedSubscriptionItem = subscriptions.find(
      subscriptionItem => subscriptionItem.subscriptionId === subscriptionId
    )
    if (selectedSubscriptionItem?.offerType === 'CPPO') {
      await dispatch(
        getActiveReceivedResaleAuthorizations({
          partnerId: currentAccountId,
          subscriptionId,
        })
      )
      await setSelectedSubscription(subscriptionId)
      // await setCppoSubscription('create')
    } else {
      setSelectedSubscription(subscriptionId)
      // Construct subscription URL with cloud/accountId if available
      const currentPath = window.location.pathname
      const basePath = currentPath.replace(
        '/flyout/private-offer/create-abo',
        ''
      )
      const subscriptionPath = `${basePath}/flyout/subscriptions/${subscriptionId}/edit`
      navigate(subscriptionPath, {
        state: {
          subscriptionId: subscriptionId,
        },
      })
    }
  }

  const loadingActiveResellerAuth = false

  return (
    <div data-testid="abo-creation-wrapper">
      <SubscriptionSelectionModal
        open={true}
        acceptSelectedSubscription={acceptSelectedSubscription}
        currentSelectedSubscriptionId={selectedSubscription}
        acceptLoading={loadingActiveResellerAuth}
        type={'MODAL'}
        hideCancelIcon={true}
        isSalesforce={true}
      />
    </div>
  )
}

export const SalesforceABOCreation: React.FC = () => {
  const { search } = useLocation()
  const { record, partnerId } = useSalesforceContext()
  const { subscriptionId } = useParams<{
    subscriptionId: string
  }>()
  const crmOpportunityId = record?.recordId || ''
  const dispatch = useDispatch()
  const cloudMarketplace = useCurrentCloudProvider()
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const externalBaseURL = `/external-apps/salesforce/partners/${partnerId}/flyout`
  // Load products if they're not already loaded (needed for Salesforce context)
  useEffect(() => {
    if (!isEmpty(partnerId) && isEmpty(products) && cloudMarketplace) {
      dispatch(getProducts(partnerId, cloudMarketplace))
    }
  }, [partnerId, products, cloudMarketplace, dispatch])

  return (
    <LayoutWrapper
      isExternal
      title="Create agreement based offer"
      isBack={false}
      actions={SelectedCloudIcon}
      isSidePanelOpened={false}
    >
      <RenewOfferSubscriptionWrapper
        search={search}
        crmOpportunityId={crmOpportunityId}
        externalBaseURL={externalBaseURL}
        isSalesforce={true}
      />
    </LayoutWrapper>
  )
}

export const SubscriptionDetail: React.FC = () => {
  return <SubscriptionDetailWrapper isBack={false} />
}

export const LightningRecordPage: React.FC = () => {
  const dispatch = useDispatch()
  const { record, crmId, partnerId } = useSalesforceContext()
  const navigate = useNavigate()
  const location = useLocation()

  const RECORD_TYPE_TO_DETAIL_PATH: Record<
    string,
    (
      basePath: string,
      labraOfferId: string,
      labraProductId: string,
      cloudProvider?: string,
      labraAccountId?: string
    ) => string
  > = {
    offer: (
      basePath,
      labraOfferId,
      labraProductId,
      _cloudProvider,
      labraAccountId
    ) => {
      let updatedBasePath = basePath
      if (labraAccountId) {
        updatedBasePath = updatedBasePath?.replace(
          /\/partners\/[^/]+/,
          `/partners/${labraAccountId}`
        )
      }
      return `${updatedBasePath}/products-listing/${labraProductId}/private-offers/${labraOfferId}/details`
    },
    'resellers agreement': (
      basePath,
      labraOfferId,
      labraProductId,
      _cloudProvider,
      labraAccountId
    ) => {
      let updatedBasePath = basePath
      if (labraAccountId) {
        updatedBasePath = updatedBasePath?.replace(
          /\/partners\/[^/]+/,
          `/partners/${labraAccountId}`
        )
      }
      return `${updatedBasePath}/products-listing/${labraProductId}/resale-authorizations/${labraOfferId}/details`
    },
    subscription: (basePath, labraOfferId) =>
      `${basePath}/subscriptions/${labraOfferId}/details`,
  }

  const recordId = record?.recordId ?? ''
  const objectAPIName = record?.objectAPIName ?? ''
  const basePath = location.pathname.replace(/\/lightningRecordPage\/?$/, '')
  const fields = {
    LabraIo__Labra_Offer__c:
      'LabraIo__Labra_Offer_Id__c,LabraIo__Record_Type__c,LabraIo__Labra_Product__r.LabraIo__Labra_Product_Id__c,LabraIo__Cloud_Provider__c,LabraIo__Labra_Account_Id__c',
    LabraIo__Labra_AWS_Referral__c:
      'LabraIo__Labra_Referral_Id__c, LabraIo__Labra_Account_Id__c',
    LabraIo__Labra_Microsoft_Referral__c:
      'LabraIo__Labra_Referral_Id__c, LabraIo__Labra_Account_Id__c',
  }

  useEffect(() => {
    const loadAndNavigate = async () => {
      const data = await dispatch(
        fetchLightningRecord({
          crmId,
          objectAPIName,
          recordId,
          fields: fields[objectAPIName as keyof typeof fields] ?? '',
        })
      )
      if (!data) return

      if (
        objectAPIName === 'LabraIo__Labra_AWS_Referral__c' ||
        objectAPIName === 'LabraIo__Labra_Microsoft_Referral__c'
      ) {
        const labraReferralId = data.labraReferralId
        if (!labraReferralId) {
          dispatch(
            updateAppAlert({
              message:
                'Referral ID is missing. Unable to navigate to the detail page.',
              messageType: 'ERROR',
              autoClose: false,
            })
          )
          return
        }
        const labraAccountId = data.labraAccountId?.trim() || partnerId
        const cloudProvider =
          objectAPIName === 'LabraIo__Labra_Microsoft_Referral__c'
            ? 'azure'
            : 'aws'
        const cosellBasePath = basePath.replace(/\/flyout\/?$/, '')
        navigate({
          pathname: `${cosellBasePath}/${buildSalesforceCosellReferralDetailsCosellSegment(
            labraReferralId,
            labraAccountId,
            cloudProvider
          )}`,
        })
        return
      }

      if (!data.labraProductId) {
        dispatch(
          updateAppAlert({
            message:
              'Product ID is missing. Unable to navigate to the detail page.',
            messageType: 'ERROR',
            autoClose: false,
          })
        )
        return
      }

      const buildPath =
        RECORD_TYPE_TO_DETAIL_PATH[data.recordType.toLowerCase()]
      if (!buildPath) return

      const detailPath = await buildPath(
        basePath,
        data.labraOfferId,
        data.labraProductId,
        data.cloudProvider,
        data.labraAccountId
      )
      if (data?.labraAccountId) {
        await dispatch(
          setCloudMarketplaceProductListing(data?.cloudProvider as CloudType)
        )
        await dispatch(
          setSelectedAccount(
            data?.labraAccountId,
            'flyout',
            data?.cloudProvider as CloudType
          )
        )
        await navigate({ pathname: detailPath })
      } else {
        navigate({ pathname: detailPath })
      }
    }
    void loadAndNavigate()
  }, [dispatch, crmId, objectAPIName, recordId, basePath, navigate, partnerId])

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '35vh',
        width: '100%',
      }}
    >
      <LoadingLogo style={{ height: 'auto' }} />
    </div>
  )
}
